import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { LinkWeight } from '@freelancer/ui/link';
import { TextSize } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

export interface TextEntity {
  readonly offset: number;
  readonly length: number;
  readonly url: string;
}

enum ContentType {
  TEXT,
  MENTION,
}

interface Content {
  readonly text: string;
  readonly type: ContentType;
  readonly url?: string;
}

@Component({
  selector: 'fl-mention',
  template: `
    <ng-container
      *ngFor="let content of contents$ | async"
      [ngSwitch]="content.type"
    >
      <ng-container *ngSwitchCase="ContentType.TEXT">
        {{ content.text }}
      </ng-container>

      <ng-container *ngSwitchCase="ContentType.MENTION">
        <fl-link
          *ngIf="content.url"
          [link]="content.url"
          [size]="TextSize.INHERIT"
          [weight]="LinkWeight.BOLD"
        >
          {{ content.text }}
        </fl-link>
      </ng-container>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MentionComponent implements OnInit {
  ContentType = ContentType;
  LinkWeight = LinkWeight;
  TextSize = TextSize;

  @Input() content$: Rx.Observable<string>;
  @Input() textEntities$: Rx.Observable<ReadonlyArray<TextEntity>>;

  contents$: Rx.Observable<ReadonlyArray<Content>>;

  ngOnInit(): void {
    this.contents$ = Rx.combineLatest([this.textEntities$, this.content$]).pipe(
      map(([textEntities, content]) => {
        let lastPosition = 0;
        const contents = [];
        textEntities.forEach(textEntity => {
          if (lastPosition < textEntity.offset) {
            const text = content.substring(lastPosition, textEntity.offset);
            if (text.length > 0) {
              contents.push({
                text,
                type: ContentType.TEXT,
              });
            }
          }
          contents.push({
            text: content.substr(textEntity.offset, textEntity.length),
            type: ContentType.MENTION,
            url: textEntity.url,
          });

          lastPosition = textEntity.offset + textEntity.length;
        });

        if (lastPosition < content.length) {
          contents.push({
            text: content.substring(lastPosition, content.length),
            type: ContentType.TEXT,
          });
        }

        return contents;
      }),
    );
  }
}
